<template>
  <v-col cols="12">
    <v-row>
      <v-col cols="12">
        <div class="search-section">
          <div class="search-header">ค้นหาที่อยู่ของคุณ</div>
          <div class="search-desc">
            แนะนำค้นด้วยตำบล หรืออำเภอ / จังหวัด — รหัสไปรษณีย์ใช้เมื่อไม่ทราบชื่อตำบล
          </div>
          <v-autocomplete v-model="selectedSearch" :items="searchResults" :search-input.sync="searchQuery"
            item-text="label" item-value="id" return-object no-filter clearable :loading="searchLoading"
            :no-data-text="searchNoDataText" placeholder="พิมพ์เพื่อค้นหา..." outlined dense hide-details="auto"
            :rules="hasAddress ? [] : [rules.required]" @change="handleSearchSelect">
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title class="search-result-text">
                  <span class="search-subdistrict">{{ item.subdistrict }}</span>
                  <span class="search-sep"> » </span>
                  <span class="search-district">{{ item.district }}</span>
                  <span class="search-sep"> » </span>
                  <span class="search-province">{{ item.province }}</span>
                  <span class="search-sep"> » </span>
                  <span class="search-zipcode">{{ item.zipcode }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </div>
      </v-col>

      <v-col cols="12">
        <span class="detail-text">ที่อยู่รายละเอียด</span>
        <v-textarea v-model.trim="model.address" v-bind="taProps" :placeholder="addressPlaceholder"
          :rules="addressRequired ? [rules.required] : []" rows="3" auto-grow />
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { searchAddress } from "@/services/api/geography";

export default {
  name: "AddressFormFields",
  props: {
    value: {
      type: Object,
      default: () => ({
        provinceId: null,
        districtId: null,
        subdistrictId: null,
        province: "",
        district: "",
        subdistrict: "",
        postcode: "",
        postalCode: "",
        address: "",
        addressDetail: "",
      }),
    },
    addressPlaceholder: {
      type: String,
      default: "บ้านเลขที่ / หมู่บ้าน / อาคาร / ซอย / ถนน",
    },
    addressRequired: { type: Boolean, default: false },
  },
  data() {
    return {
      model: this.getModelFromValue(this.value),
      taProps: {
        outlined: true,
        hideDetails: "auto",
        dense: true,
        autoGrow: true,
      },
      rules: {
        required: (v) => (!!v && String(v).trim() !== "") || "กรุณากรอกข้อมูล",
      },
      searchQuery: "",
      searchResults: [],
      selectedSearch: null,
      searchLoading: false,
      searchTimer: null,
    };
  },
  computed: {
    hasAddress() {
      return !!(this.model.provinceId && this.model.districtId && this.model.subdistrictId);
    },
    searchNoDataText() {
      if (!this.searchQuery || this.searchQuery.length < 2)
        return "พิมพ์อย่างน้อย 2 ตัวอักษร";
      if (this.searchLoading) return "กำลังค้นหา...";
      return "ไม่พบข้อมูล";
    },
  },
  watch: {
    searchQuery(val) {
      clearTimeout(this.searchTimer);
      if (this.selectedSearch && val === this.selectedSearch.label) return;
      if (!val || val.length < 2) {
        if (!this.selectedSearch) this.searchResults = [];
        return;
      }
      this.searchTimer = setTimeout(() => this.doSearch(val), 300);
    },
    value: {
      deep: true,
      handler(v) {
        this.model = this.getModelFromValue(v || {});
        if (this.hasAddress) {
          this.syncSearchDisplay();
        }
      },
    },
    model: {
      deep: true,
      handler() {
        this.emitValue();
      },
    },
  },
  mounted() {
    if (this.hasAddress) {
      this.syncSearchDisplay();
    }
  },
  methods: {
    getModelFromValue(v) {
      const postcode = v.postcode ?? v.postalCode ?? "";
      const address = v.address ?? v.addressDetail ?? "";
      const subdistrict = v.subdistrict ?? v.subDistrict ?? "";
      return {
        provinceId: v.provinceId ?? null,
        districtId: v.districtId ?? null,
        subdistrictId: v.subdistrictId ?? null,
        province: v.province ?? "",
        district: v.district ?? "",
        subdistrict,
        postcode: String(postcode).trim(),
        address: String(address).trim(),
      };
    },
    emitValue() {
      const out = {
        ...this.model,
        postalCode: this.model.postcode,
        addressDetail: this.model.address,
        subDistrict: this.model.subdistrict,
      };
      this.$emit("input", out);
    },
    syncSearchDisplay() {
      const m = this.model;
      if (m.subdistrict || m.district || m.province) {
        const sub = m.subdistrict || "";
        const dist = m.district || "";
        const prov = m.province || "";
        const zip = m.postcode || "";
        const label = [sub, dist, prov, zip].filter(Boolean).join(" » ");

        this.selectedSearch = {
          id: m.subdistrictId || `temp-${Date.now()}`,
          label,
          subDistrictId: m.subdistrictId,
          districtId: m.districtId,
          provinceId: m.provinceId,
          subdistrict: sub,
          district: dist,
          province: prov,
          zipcode: zip,
        };
        this.searchResults = [this.selectedSearch];
        this.searchQuery = ""; // Clear query to show selection
      }
    },

    async doSearch(keyword) {
      if (!keyword || keyword.trim().length < 2) return;
      this.searchLoading = true;
      try {
        const results = await searchAddress(keyword);
        this.searchResults = results.map((item) => {
          const district = item.district || {};
          const province = district.province || {};
          const sub = item.nameTh || "";
          const dist = district.nameTh || "";
          const prov = province.nameTh || "";
          const zip = item.zipcode || "";
          return {
            id: item.subDistrictId,
            label: `${sub} » ${dist} » ${prov} » ${zip}`,
            subDistrictId: item.subDistrictId,
            districtId: district.districtId,
            provinceId: province.provinceId,
            subdistrict: sub,
            district: dist,
            province: prov,
            zipcode: zip,
          };
        });
      } catch (e) {
        console.error("[AddressFormFields] doSearch error", e);
        this.searchResults = [];
      } finally {
        this.searchLoading = false;
      }
    },
    handleSearchSelect(item) {
      if (!item) {
        this.clearAddress();
        return;
      }
      this.model.provinceId = item.provinceId;
      this.model.province = item.province;
      this.model.districtId = item.districtId;
      this.model.district = item.district;
      this.model.subdistrictId = item.subDistrictId ?? item.subdistrictId;
      this.model.subdistrict = item.subdistrict;
      this.model.postcode = item.zipcode;
      this.searchQuery = "";
      this.emitValue();
    },
    clearAddress() {
      this.model.provinceId = null;
      this.model.districtId = null;
      this.model.subdistrictId = null;
      this.model.province = "";
      this.model.district = "";
      this.model.subdistrict = "";
      this.model.postcode = "";
      this.selectedSearch = null;
      this.searchQuery = "";
      this.searchResults = [];
      this.emitValue();
    },

    /**
     * (ทางเลือก) ผูกผลจาก Google ให้ฟอร์ม — ปกติไม่ใช้เพราะแผนที่กับฟอร์มแยกกัน
     * ถ้าเปิด sync ใน GoogleMapPicker (`sync-address-fields`) ค่อยเรียกจาก parent
     */
    async applyResolvedMapAddress(data) {
      if (!data || typeof data !== "object") return;
      const pc = data.postalCode ?? data.postcode ?? "";
      if (pc !== "" && pc != null) {
        this.model.postcode = String(pc).trim();
      }
      const detail = (data.addressDetail ?? "").trim();
      if (detail) {
        this.model.address = detail;
      }
      this.emitValue();
      await this.$nextTick();
      await this.resolveFromNames({
        province: data.province,
        district: data.district,
        subDistrict: data.subDistrict ?? data.subdistrict,
        postalCode: data.postalCode ?? data.postcode,
        formattedAddress: data.formattedAddress,
      });
    },

    /** เรียกจาก parent หรือ applyResolvedMapAddress: resolve IDs จากชื่อ (ค้นหาหลักด้วยตำบล ไม่ใช้รหัสไปรษณีย์เป็นตัวนำ) */
    async resolveFromNames({ province, district, subDistrict, postalCode, formattedAddress } = {}) {
      const pName = (province || "").replace(/จังหวัด/g, "").trim();
      const dName = (district || "").replace(/อำเภอ|เขต/g, "").trim();
      const sName = (subDistrict || "").replace(/ตำบล|แขวง/g, "").trim();
      const zip = (postalCode ?? this.model.postcode ?? "").toString().trim();

      const isThaiText = (s) => /[\u0E00-\u0E7F]/.test(s);
      const isPostalOnly = (s) => /^\d{5}(-\d{4})?$/.test(String(s).trim());

      // คำค้นหา API: ตำบล → อำเภอ → จังหวัด เท่านั้น (ไม่ใส่รหัสไปรษณีย์ในขั้นแรก)
      let q = (sName || dName || pName || "").trim();
      if (!q && formattedAddress) {
        const segments = formattedAddress.split(",").map((s) => s.trim()).filter(Boolean);
        q =
          segments.find((s) => isThaiText(s) && !isPostalOnly(s)) ||
          segments.find((s) => isThaiText(s)) ||
          "";
      }
      let usedZipAsQuery = false;
      if (!q && zip && isPostalOnly(zip)) {
        q = zip;
        usedZipAsQuery = true;
      }
      if (!q) return;

      try {
        let results = await searchAddress(q);
        if (!results.length && formattedAddress && q !== formattedAddress && !usedZipAsQuery) {
          const slice = formattedAddress.slice(0, 80);
          if (slice && !isPostalOnly(slice)) {
            results = await searchAddress(slice);
          }
        }
        if (!results.length) return;

        const rowMeta = (r) => {
          const d = r.district || {};
          const p = d.province || {};
          const subMatch =
            !sName ||
            (r.nameTh || "").includes(sName) ||
            sName.includes(r.nameTh || "");
          const distMatch =
            !dName ||
            (d.nameTh || "").includes(dName) ||
            dName.includes(d.nameTh || "");
          const provMatch =
            !pName ||
            (p.nameTh || "").includes(pName) ||
            pName.includes(p.nameTh || "");
          const zipMatch = !zip || String(r.zipcode) === String(zip);
          return { subMatch, distMatch, provMatch, zipMatch, d, p };
        };

        let match =
          results.find((r) => {
            const { subMatch, distMatch, provMatch, zipMatch } = rowMeta(r);
            if (!subMatch || !distMatch || !provMatch) return false;
            if (sName) return true;
            if (zip) return zipMatch;
            return true;
          }) ||
          results.find((r) => {
            const { subMatch, distMatch, provMatch } = rowMeta(r);
            return subMatch && distMatch && provMatch;
          }) ||
          (sName
            ? results.find((r) => rowMeta(r).subMatch)
            : null) ||
          (zip
            ? results.find((r) => rowMeta(r).zipMatch)
            : null) ||
          results[0];

        const dd = match.district || {};
        const pp = dd.province || {};
        this.model.provinceId = pp.provinceId;
        this.model.province = pp.nameTh || "";
        this.model.districtId = dd.districtId;
        this.model.district = dd.nameTh || "";
        this.model.subdistrictId = match.subDistrictId;
        this.model.subdistrict = match.nameTh || "";
        if (match.zipcode) {
          this.model.postcode = String(match.zipcode).trim();
        }

        this.syncSearchDisplay();
        this.emitValue();
      } catch (e) {
        console.error("[AddressFormFields] resolveFromNames error", e);
      }
    },
    syncNamesForSubmit() {
      this.emitValue();
    },
    ensureOptionsLoaded() {
      if (this.hasAddress) this.syncSearchDisplay();
    },
  },
};
</script>

<style scoped>
.search-section {
  margin-bottom: 4px;
}

.search-header {
  font-size: 16px;
  font-weight: 600;
  color: #1f2a39;
  margin-bottom: 4px;
}

.search-desc {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 8px;
}

.search-result-text {
  font-size: 14px;
  color: #374151;
}

.search-sep {
  color: #9ca3af;
  margin: 0 2px;
}

.search-zipcode {
  color: #2563eb;
}

.detail-text {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #1f2a39;
  line-height: 1.4;
  margin-bottom: 2px;
}
</style>
